import React, { useState, useCallback } from "react";
import Layout from "@/components/Layout";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import Turnstile from "@/components/Turnstile";
import { Mail, Phone, MapPin, Clock, Send } from "lucide-react";
import { toast } from "sonner";

const Contact = () => {
  const [form, setForm] = useState({ name: "", email: "", subject: "", message: "" });
  const [loading, setLoading] = useState(false);
  const [tsToken, setTsToken] = useState("");
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const onTsToken = useCallback((t) => setTsToken(t), []);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL || "(missing REACT_APP_BACKEND_URL)";
      // eslint-disable-next-line no-console
      // [removed console.info]
      const { data } = await api.post("/enquiries", {
        type: "contact",
        name: form.name,
        email: form.email,
        subject: form.subject,
        message: form.message,
        form_data: {},
        turnstile_token: tsToken,
      });
      // Diagnostics stay in console for admins/devs — never surfaced to customers.
      // eslint-disable-next-line no-console
      // [removed console.info]
      toast.success(data.message || `Thank you, ${form.name.split(" ")[0]}! Your enquiry has been received.`, {
        description: data.reference_no ? `Reference: ${data.reference_no}` : undefined,
        duration: 8000,
      });
      setForm({ name: "", email: "", subject: "", message: "" });
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error("[Contact form] submission FAILED:", err);
      if (err?.response?.status === 429) {
        toast.error("Too many attempts", { description: err.response?.data?.detail || "Please wait a minute and try again." });
      } else {
        toast.error("Failed to send", { description: err.response?.data?.detail || "Please try again." });
      }
    } finally {
      setLoading(false);
    }
  };

  React.useEffect(() => { document.title = "Contact — Dream Home Engineers"; }, []);

  return (
    <Layout>
      <section className="max-w-[1400px] mx-auto px-6 lg:px-10 py-14 lg:py-20 grid grid-cols-1 lg:grid-cols-12 gap-14">
        <div className="lg:col-span-5">
          <div className="overline text-accent">Contact</div>
          <h1 className="font-display text-5xl lg:text-6xl mt-4 leading-[0.95] tracking-tighter">Let's talk about your home.</h1>
          <p className="mt-6 text-lg text-muted-foreground">
            Have a question about a plan? Want a fixed-fee quote for custom work? Our team responds to every inquiry within 24 hours.
          </p>
          <div className="mt-12 space-y-6">
            {[
              { icon: Mail, label: "Email", value: "nagesh@dreamhomeengineers.com" },
              { icon: Mail, label: "Alternate email", value: "bchsrnag@gmail.com" },
              { icon: Phone, label: "Phone", value: "+91 95508 99555" },
              { icon: Phone, label: "Landline", value: "+91 40 4850 2772" },
              { icon: MapPin, label: "Office", value: "Dream Home Engineers, 21-205, Valli Nilayam, Balajinagar, Kukatpally, Hyderabad, Telangana 500072, India" },
              { icon: Clock, label: "Business hours", value: "Mon – Sat · 9:30 AM – 7:00 PM IST" },
            ].map(({ icon: Icon, label, value }) => (
              <div key={label} className="flex gap-4">
                <div className="w-11 h-11 shrink-0 bg-primary text-primary-foreground flex items-center justify-center"><Icon size={18} /></div>
                <div>
                  <div className="overline text-muted-foreground">{label}</div>
                  <div className="text-base mt-1">{value}</div>
                </div>
              </div>
            ))}
          </div>

          {/* Google Map */}
          <div className="mt-10 border border-border overflow-hidden" data-testid="office-map">
            <iframe
              title="Dream Home Engineers office location"
              src="https://www.google.com/maps?q=Kukatpally%2C+Hyderabad%2C+Telangana+500072&output=embed"
              width="100%"
              height="280"
              style={{ border: 0 }}
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
            />
          </div>
        </div>

        <div className="lg:col-span-7">
          <form onSubmit={submit} className="border border-border p-8 lg:p-10 space-y-5" data-testid="contact-form">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              <div>
                <Label className="overline text-muted-foreground">Name</Label>
                <Input required value={form.name} onChange={(e) => set("name", e.target.value)} className="rounded-none mt-2 h-12" data-testid="contact-name" />
              </div>
              <div>
                <Label className="overline text-muted-foreground">Email</Label>
                <Input required type="email" value={form.email} onChange={(e) => set("email", e.target.value)} className="rounded-none mt-2 h-12" data-testid="contact-email" />
              </div>
            </div>
            <div>
              <Label className="overline text-muted-foreground">Subject</Label>
              <Input value={form.subject} onChange={(e) => set("subject", e.target.value)} placeholder="e.g. Question about The Meridian" className="rounded-none mt-2 h-12" />
            </div>
            <div>
              <Label className="overline text-muted-foreground">Message</Label>
              <Textarea rows={6} required value={form.message} onChange={(e) => set("message", e.target.value)} className="rounded-none mt-2" data-testid="contact-message" />
            </div>
            <Turnstile onToken={onTsToken} action="contact" />
            <Button type="submit" disabled={loading} data-testid="contact-submit" className="rounded-none bg-accent hover:bg-accent/90 h-14 px-10">
              {loading ? "Sending…" : "Send message"} <Send size={16} className="ml-2" />
            </Button>
          </form>
        </div>
      </section>
    </Layout>
  );
};

export default Contact;
